import { io as Client, Socket as ClientSocket } from 'socket.io-client';
import { EventsMap } from 'socket.io/dist/typed-events';
import { AuthError, BaseRealtimeClientType, ClientRealtimeOptionsType, ClientEventHandlersTypes } from '../types';
import { v4 as uuidv4 } from 'uuid';

type RealtimeClientParametersType<
  STC extends EventsMap,
  CTS extends EventsMap
> = {
  serverUrl: string,
  eventHandlers: ClientEventHandlersTypes<STC, CTS>,
  options?: ClientRealtimeOptionsType | null,
  stateCallback?: BaseRealtimeClientType['stateCallback'] | null
};

export type CreateRealtimeClientType<
  STC extends EventsMap,
  CTS extends EventsMap
> = {
  serverUrl: RealtimeClientParametersType<STC, CTS>['serverUrl'],
  options?: RealtimeClientParametersType<STC, CTS>['options'],
  stateCallback?: RealtimeClientParametersType<STC, CTS>['stateCallback']
};

export default class RealtimeClient<
  STC extends EventsMap,
  CTS extends EventsMap
> {
  private socketClient?: ClientSocket<
    STC,
    CTS
  >
  private serverUrl: RealtimeClientParametersType<STC, CTS>['serverUrl'];
  private token?: string;
  private options: ClientRealtimeOptionsType;
  private eventHandlers: RealtimeClientParametersType<STC, CTS>['eventHandlers'];
  private stateCallback: BaseRealtimeClientType['stateCallback'] | null = null;

  private uniqueId: string = uuidv4();

  private connectionAttempts: number = 1;
  private hardDeniedAttempts: number = 1;
  private unauthorizedAttempts: number = 1;
  private isRetryingHardDenied: boolean = false;
  private isRetryingUnauthorized: boolean = false;
  private authorized: boolean = false;

  private retryTimeoutId?: NodeJS.Timeout;
  private activeRetry?: string;

  constructor(
    serverUrl: RealtimeClientParametersType<STC, CTS>['serverUrl'],
    eventHandlers: RealtimeClientParametersType<STC, CTS>['eventHandlers'],
    options?: RealtimeClientParametersType<STC, CTS>['options'],
    stateCallback?: RealtimeClientParametersType<STC, CTS>['stateCallback'],
  ) {
    this.options = options ?? {
      reconnection: true,
      // reconnectionAttempts: 5,
      reconnectionDelay: 1000, // 1 second
      timeout: 20000, // 20 seconds
      autoConnect: false,
    };
    this.eventHandlers = eventHandlers;
    this.stateCallback = stateCallback ?? null;
    this.serverUrl = serverUrl;
  }

  public connect(token?: string): void {
    if (this.authorized) {
      console.error('Already connected.');
    }
    this.initializeSocket(token);
  }

  public disconnect(): void {
    // console.log('this.socketClient', this.socketClient);
    if (this.socketClient) {
      this.authorized = false;
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
      }
      this.resetConnectionCounters();
      this.stateCallback?.('not_initialized');
      this.socketClient?.disconnect();
    }
  }

  private countersSwitched(active: string): void {
    if (this.activeRetry !== active) {
      this.resetConnectionCounters();
      this.activeRetry = active;
    }
  };

  private setTimeoutJob(func: () => void, count: number): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    const retryDelay = this.getRetryDelay(count);
    // console.log(`Retrying connection in ${retryDelay / 1000} seconds...`);

    this.retryTimeoutId = setTimeout(() => {
      func();
    }, retryDelay);
  }

  private initializeSocket(token?: string): void {
    if (!token || token.trim() === '') {
      console.error('Token is missing.');
    }
    this.token = token;

    const containsLocalhost = this.serverUrl.includes('localhost'); 

    const _socket = Client(this.serverUrl, {
      path: containsLocalhost ? undefined : '/realtime',
      auth: {
        token: this.token,
        session_id: this.uniqueId,
      },
      reconnection: this.options.reconnection,
      reconnectionAttempts: this.options.reconnectionAttempts,
      reconnectionDelay: this.options.reconnectionDelay,
      timeout: this.options.timeout,
      autoConnect: this.options.autoConnect,
    });
    _socket.on('connect_error', (err) => {
      const error: AuthError = err as AuthError;
      if (error?.data?.code === '401') {
        this.retryUnauthorizedConnection();
      } else if (!_socket.active) {
        if (error?.data?.code === '500') {
          this.stateCallback?.('server_error', `reconnect attempts: ${this.hardDeniedAttempts}`);
          this.retryConnection();
        } else {
          this.stateCallback?.('connection_failed', `reconnect attempts: ${this.hardDeniedAttempts}`);
          this.retryConnection();
        }
      }
      else {
        this.stateCallback?.('connection_failed', `connect attempts: ${this.connectionAttempts}`);
        this.connectionAttempts++;
      }
    });

    _socket.on('connect', () => {
      this.authorized = true;
      this.resetConnectionCounters();
      this.stateCallback?.('connected');
    });

    _socket.once('connect', () => {
      Object.keys(this.eventHandlers ?? {}).forEach((eventName: string) => {
        const handler = this.eventHandlers[eventName as keyof typeof this.eventHandlers]?.handler;
        // @ts-ignore
        _socket.on(eventName, (...args: any[]) => handler(_socket, ...args));
      });
    });

    _socket.on('disconnect', (reason) => {
      // console.log('Disconnected from server:', reason);
      this.authorized = false;
      if (reason === 'io client disconnect') {
        return;
      }

      this.stateCallback?.('disconnected', `reconnect attempts: ${this.hardDeniedAttempts}`);
      this.retryConnection();
    });

    this.socketClient = _socket;
    this.socketClient.connect();
  }

  private retryConnection(): void {
    console.log('retryConnection');
    if (this.isRetryingHardDenied) {
      return;
    }
    this.countersSwitched('hardDenied');
    this.isRetryingHardDenied = true;
    this.hardDeniedAttempts++;

    this.setTimeoutJob(
      () => {
        this.isRetryingHardDenied = false;
        this.connect(this.token);
      },
      this.hardDeniedAttempts
    );
  }

  private retryUnauthorizedConnection(): void {
    this.stateCallback?.('auth_failed', `authorization attempts: ${this.unauthorizedAttempts}`);
    if (this.isRetryingUnauthorized) {
      return;
    }
    this.countersSwitched('auth');
    this.isRetryingUnauthorized = true;
    this.unauthorizedAttempts++;

    this.setTimeoutJob(
      () => {
        this.isRetryingUnauthorized = false;
        this.connect(this.token);
      },
      this.unauthorizedAttempts
    );
  }

  private getRetryDelay(attempt: number): number {
    const baseDelay = attempt * 1000;
    const maxDelay = Math.min(2000, baseDelay);
    // const maxDelay = Math.min(10000, baseDelay * Math.pow(2, attempt - 2)); // Cap the delay at 30 seconds
    const jitter = Math.random() * 1000; // Add a jitter between 0 and 1 second
    return maxDelay + jitter;
  }

  private resetConnectionCounters(): void {
    this.connectionAttempts = 1;
    this.unauthorizedAttempts = 1;
    this.hardDeniedAttempts = 1;
    // console.log('Connection counters reset');
  }

  public emit<K extends keyof CTS>(eventName: K, ...args: Parameters<CTS[K]>): void {
    if (this.socketClient?.connected) {
      this.socketClient.emit(eventName as string, ...(args as Parameters<CTS[string]>));
      return;
    }
    if (!this.socketClient) console.error('Socket client is not initialized.');
    if (!this.socketClient?.connected) console.error('Socket client is not connected.');
  }

  public socket(): ClientSocket<STC, CTS> {
    return this.socketClient!;
  }

  public isLoggedIn(): boolean {
    return this.socketClient?.connected ?? false;
  }
}
